import type { GenericTask } from '@compass/client/types/generic_task';
import { GenericTaskStatus } from '@compass/client/types/generic_task';
import { Button } from '@components/ui/button';
import { Copy } from 'lucide-react';
import React from 'react';

import { FormattedDateTime } from '@/components/FormattedDateTime';
import {
  getGenericTaskStatusDisplayText,
  getGenericTaskTypeDisplayText,
} from '@/utils/callsTabs/statusDisplay';
import { copyToClipboard } from '@/utils/clipboard';

import type { PopoverContentProps, TabLabelContext, TabTypeHandler } from './types';

const getLabel = (_data: GenericTask, _context: TabLabelContext): string => {
  return 'Task';
};

const GenericTaskPopoverContent: React.FC<PopoverContentProps<GenericTask>> = ({
  data: genericTask,
  isHumanAudit,
  onRetryTask,
}) => {
  return (
    <div className="space-y-2 text-sm">
      <div className="flex justify-between gap-8 items-center">
        <span className="text-muted-foreground">Task ID:</span>
        <div className="flex items-center gap-2">
          <span className="font-mono text-xs">{genericTask.id}</span>
          {genericTask.id && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => copyToClipboard(genericTask.id!)}
              className="h-6 w-6 p-0"
            >
              <Copy className="w-3 h-3" />
            </Button>
          )}
        </div>
      </div>
      <div className="flex justify-between gap-8">
        <span className="text-muted-foreground">Type:</span>
        <span>{getGenericTaskTypeDisplayText(genericTask.type)}</span>
      </div>
      <div className="flex justify-between gap-8">
        <span className="text-muted-foreground">Status:</span>
        <span>{getGenericTaskStatusDisplayText(genericTask.status)}</span>
      </div>
      {genericTask.input?.recipient && (
        <div className="flex justify-between gap-8">
          <span className="text-muted-foreground">Recipient:</span>
          <span>{genericTask.input.recipient}</span>
        </div>
      )}
      {genericTask.input?.subject && (
        <div className="flex justify-between gap-8">
          <span className="text-muted-foreground">Subject:</span>
          <span>{genericTask.input.subject}</span>
        </div>
      )}
      {genericTask.input?.message && (
        <div className="flex justify-between gap-8">
          <span className="text-muted-foreground">Message:</span>
          <span>{genericTask.input.message}</span>
        </div>
      )}
      {genericTask.error && (
        <div className="flex justify-between gap-8">
          <span className="text-muted-foreground">Error:</span>
          <span className="text-destructive">{genericTask.error}</span>
        </div>
      )}
      {genericTask.created_at && (
        <div className="flex justify-between gap-8">
          <span className="text-muted-foreground">Created:</span>
          <FormattedDateTime value={genericTask.created_at} />
        </div>
      )}
      {genericTask.updated_at && (
        <div className="flex justify-between gap-8">
          <span className="text-muted-foreground">Updated:</span>
          <FormattedDateTime value={genericTask.updated_at} />
        </div>
      )}

      {genericTask.status === GenericTaskStatus.GENERIC_TASK_STATUS_FAILED &&
        isHumanAudit &&
        onRetryTask && (
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={() => onRetryTask(genericTask.id!)}>
              Retry Task
            </Button>
          </div>
        )}
    </div>
  );
};

export const genericTaskHandler: TabTypeHandler<GenericTask> = {
  isDisabled: true,
  getLabel,
  getStatusLabel: data => getGenericTaskStatusDisplayText(data.status),
  PopoverContent: GenericTaskPopoverContent,
};
